import { Component } from '@theme/component';

/**
 * Bandeau d’annonce Poke-Geek avec autoplay continu et navigation manuelle.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #timer = undefined;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.current = 0;
    window.setTimeout(() => this.play(), 50);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
    this.restart();
  }

  previous() {
    this.current -= 1;
    this.restart();
  }

  play() {
    this.stop();

    const slides = this.refs.slides ?? [];
    const delay = this.autoplayInterval;

    if (slides.length < 2 || !delay || document.hidden) return;

    this.#timer = window.setInterval(() => {
      this.current += 1;
    }, delay);
  }

  stop() {
    if (this.#timer !== undefined) {
      window.clearInterval(this.#timer);
      this.#timer = undefined;
    }
  }

  restart() {
    this.play();
  }

  pause() {
    this.stop();
  }

  resume() {
    this.play();
  }

  get autoplayInterval() {
    const seconds = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    let index = value % slides.length;
    if (index < 0) index += slides.length;

    this.#current = index;

    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.toggleAttribute('data-active', isActive);
    });
  }

  #handleVisibilityChange = () => {
    if (document.hidden) this.stop();
    else this.play();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
